import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Trash2, AlertTriangle, CheckCircle, Loader } from 'lucide-react';
import { chatService } from '../services/chatService';
import { bookingService } from '../services/bookingService';
import toast from 'react-hot-toast';

const DataCleanup = () => {
  const [loading, setLoading] = useState(false);
  const [deletingChats, setDeletingChats] = useState(false);
  const [deletingBookings, setDeletingBookings] = useState(false);

  const handleDeleteAllChats = async () => {
    if (!window.confirm('Are you sure you want to delete ALL chats? This action cannot be undone!')) {
      return;
    }

    setDeletingChats(true);
    try {
      const result = await chatService.deleteAllChats();
      if (result.success) {
        // Clear localStorage cache
        localStorage.removeItem('userChats');
        localStorage.removeItem('recentActivity');
        toast.success(result.message + ' Browser cache cleared!');
      } else {
        toast.error(result.error || 'Failed to delete chats');
      }
    } catch (error) {
      console.error('Error deleting chats:', error);
      toast.error('Failed to delete chats');
    } finally {
      setDeletingChats(false);
    }
  };

  const handleDeleteAllBookings = async () => {
    if (!window.confirm('Are you sure you want to delete ALL bookings? This action cannot be undone!')) {
      return;
    }

    setDeletingBookings(true);
    try {
      const result = await bookingService.deleteAllBookings();
      if (result.success) {
        // Clear localStorage cache
        localStorage.removeItem('userBookings');
        localStorage.removeItem('recentActivity');
        toast.success(result.message + ' Browser cache cleared!');
      } else {
        toast.error(result.error || 'Failed to delete bookings');
      }
    } catch (error) {
      console.error('Error deleting bookings:', error);
      toast.error('Failed to delete bookings');
    } finally {
      setDeletingBookings(false);
    }
  };

  const handleDeleteAllData = async () => {
    if (!window.confirm('Are you sure you want to delete ALL chats AND bookings? This action cannot be undone!')) {
      return;
    }

    setLoading(true);
    try {
      // Delete all chats
      const chatsResult = await chatService.deleteAllChats();
      if (!chatsResult.success) {
        toast.error('Failed to delete chats: ' + chatsResult.error);
        return;
      }

      // Delete all bookings
      const bookingsResult = await bookingService.deleteAllBookings();
      if (!bookingsResult.success) {
        toast.error('Failed to delete bookings: ' + bookingsResult.error);
        return;
      }

      // Clear all localStorage cache
      localStorage.removeItem('userChats');
      localStorage.removeItem('userBookings');
      localStorage.removeItem('recentActivity');

      toast.success('All data deleted successfully! Browser cache cleared!');
    } catch (error) {
      console.error('Error deleting all data:', error);
      toast.error('Failed to delete all data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <Trash2 className="w-8 h-8 text-red-600" />
            <h1 className="text-2xl font-bold text-gray-900">Data Cleanup</h1>
          </div>

          <div className="space-y-6">
            {/* Warning */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <h3 className="text-lg font-semibold text-red-800">⚠️ Warning</h3>
              </div>
              <p className="text-red-700 mt-2">
                This page allows you to delete all data from the database. These actions are irreversible and will permanently remove all chats and bookings.
              </p>
            </div>

            {/* Delete Chats */}
            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete All Chats</h3>
              <p className="text-gray-600 mb-4">
                This will delete all chat conversations and their messages from the database.
              </p>
              <button
                onClick={handleDeleteAllChats}
                disabled={deletingChats}
                className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                {deletingChats ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Deleting Chats...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete All Chats
                  </>
                )}
              </button>
            </div>

            {/* Delete Bookings */}
            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete All Bookings</h3>
              <p className="text-gray-600 mb-4">
                This will delete all booking records from the database.
              </p>
              <button
                onClick={handleDeleteAllBookings}
                disabled={deletingBookings}
                className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                {deletingBookings ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Deleting Bookings...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete All Bookings
                  </>
                )}
              </button>
            </div>

            {/* Delete All Data */}
            <div className="border-2 border-red-300 bg-red-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-red-800 mb-4">Delete All Data</h3>
              <p className="text-red-700 mb-4">
                This will delete ALL chats AND bookings in one operation. Use this to completely reset the database.
              </p>
              <button
                onClick={handleDeleteAllData}
                disabled={loading}
                className="bg-red-800 hover:bg-red-900 disabled:bg-red-600 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors font-semibold"
              >
                {loading ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Deleting All Data...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-5 h-5" />
                    Delete All Data
                  </>
                )}
              </button>
            </div>

            {/* Success Message */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 hidden" id="successMessage">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-green-800 font-semibold">Success!</span>
              </div>
              <p className="text-green-700 mt-1">
                Data has been successfully deleted. You can now start fresh with new bookings and chats.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataCleanup;
